// hooks/useMessageProcessing.js
import { useMemo } from 'react';
// Assuming users data might be fetched/managed elsewhere or passed in if needed for sender lookup fallback
// For now, relies on sender info embedded in message object by formatMessageFromApi
// import { users } from '../chatData';

export const useMessageProcessing = (
    activeConversationId, activeMessages, pinnedMessageId,
    replyingToMessage, currentUserId
) => {

    const pinnedMessage = useMemo(() => {
        if (!pinnedMessageId) return null;
        return activeMessages.find(msg => msg.id === pinnedMessageId) || null;
    }, [pinnedMessageId, activeMessages]);

    // Process messages for ChatBody display (add sender/isOutgoing/replyTo)
    const processedMessages = useMemo(() => {
        if (!activeMessages || activeMessages.length === 0) return [];
        return activeMessages.map(msg => {
            if (msg.type === 'date') return msg;
            const isOutgoing = msg.senderId === currentUserId; // Compare with numeric ID potentially
            let replyTo = null;
            if (msg.replyToId) {
                const originalMsg = activeMessages.find(m => m.id === msg.replyToId);
                if (originalMsg && originalMsg.type !== 'date') {
                    // Use sender info from original message if available
                    const originalSenderName = originalMsg.sender?.id === currentUserId ? 'You' : originalMsg.sender?.name || 'Unknown';
                    let originalText = originalMsg.text || '';
                    if (!originalText) { // Generate preview for non-text
                        if (originalMsg.type === 'audio') originalText = 'Audio Message';
                        else if (originalMsg.attachments?.length > 0) {
                            const firstAttach = originalMsg.attachments[0];
                            originalText = firstAttach.name || `${firstAttach.type} Attachment`;
                            if (originalMsg.attachments.length > 1) originalText += ` (+${originalMsg.attachments.length - 1})`;
                         }
                        else originalText = 'Message';
                    }
                    replyTo = { senderName: originalSenderName, originalText: originalText };
                }
            }
            // Return message with sender object (already formatted), isOutgoing, and replyTo
            return { ...msg, isOutgoing, replyTo };
        });
    }, [activeMessages, currentUserId]);

    // Prepare data for pinned message bar
    const processedPinnedMessage = useMemo(() => {
        if (!pinnedMessage) return null;
        let text = pinnedMessage.text || '';
        if (!text) {
            if (pinnedMessage.type === 'audio') text = 'Audio Message';
            else if (pinnedMessage.attachments?.length > 0) text = pinnedMessage.attachments[0].name || `${pinnedMessage.attachments[0].type} Attachment`;
            else text = 'Message';
        }
        return { id: pinnedMessage.id, text: text };
    }, [pinnedMessage]);

    // Prepare data for reply preview in input
    const processedReplyPreview = useMemo(() => {
        if (!replyingToMessage) return null;
        let text = replyingToMessage.text || '';
         if (!text) {
            if (replyingToMessage.type === 'audio') text = 'Audio Message';
            else if (replyingToMessage.attachments?.length > 0) text = replyingToMessage.attachments[0].name || `${replyingToMessage.attachments[0].type} Attachment`;
            else text = 'Message';
        }
        const senderName = replyingToMessage.sender?.id === currentUserId ? 'You' : replyingToMessage.sender?.name || 'Unknown';
        return { id: replyingToMessage.id, senderName: senderName, text: text };
    }, [replyingToMessage, currentUserId]);

    return { processedMessages, processedPinnedMessage, processedReplyPreview, pinnedMessage };
};